import { useCallback, type ClipboardEvent } from "react";
import { toast } from "../lib/toast";
import { getCharCountColor, getCharCountStatus } from "../lib/constants";

interface CharLimitIndicatorProps {
  current: number;
  max: number;
  className?: string;
}

/**
 * Indicador visual de limite de caracteres.
 * Aparece somente quando o texto atinge 80% do limite.
 * Muda de cor: cinza → âmbar (≥90%) → vermelho (excedido).
 */
export function CharLimitIndicator({ current, max, className = "" }: CharLimitIndicatorProps) {
  const ratio = current / max;

  // Só mostra a partir de 80% do limite
  if (ratio < 0.8) return null;

  const status = getCharCountStatus(current, max);
  const colorClass = getCharCountColor(current, max);

  return (
    <span
      className={`text-[11px] tabular-nums transition-colors ${colorClass} ${className}`}
      role="status"
      aria-live="polite"
    >
      {current}/{max}
      {status === "warning" && " ⚠"}
      {status === "exceeded" && " ✕"}
    </span>
  );
}

/**
 * Hook para criar um onChange com limite de caracteres + aviso de truncagem no paste.
 * 
 * Uso:
 *   const { onChange, onPaste } = useCharLimitHandlers(maxLength, setValue);
 *   <textarea onChange={onChange} onPaste={onPaste} />
 */
export function useCharLimitHandlers(
  maxLength: number,
  setValue: (value: string) => void
) {
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (value.length <= maxLength) {
        setValue(value);
      } else {
        // Truncar e atualizar
        setValue(value.slice(0, maxLength));
      }
    },
    [maxLength, setValue]
  );

  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const pastedText = e.clipboardData.getData("text");
      const currentValue = target.value;
      const selStart = target.selectionStart ?? currentValue.length;
      const selEnd = target.selectionEnd ?? currentValue.length;

      // Calcular valor resultante
      const before = currentValue.slice(0, selStart);
      const after = currentValue.slice(selEnd);
      const resultText = before + pastedText + after;

      if (resultText.length > maxLength) {
        e.preventDefault();
        // Truncar para caber no limite
        const availableSpace = maxLength - before.length - after.length;
        const truncatedPaste = pastedText.slice(0, Math.max(0, availableSpace));
        const finalText = before + truncatedPaste + after;
        setValue(finalText.slice(0, maxLength));

        const charsRemoved = resultText.length - maxLength;
        toast.warning(
          `Texto colado foi reduzido em ${charsRemoved} caractere${charsRemoved !== 1 ? "s" : ""} para caber no limite de ${maxLength}.`,
          4000
        );
      }
    },
    [maxLength, setValue]
  );

  return { onChange, onPaste };
}
